<template>
    <div>
        <div ref="printPdf">
            <!-- Header -->
            <div style="margin-bottom: 20px;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div>
                        <p style="font-size: 18px; font-weight: bold; margin: 0 0 4px 0; color: #333;">
                            BAY ATLANTIC UNIVERSITY
                        </p>
                        <p style="font-size: 16px; font-weight: bold; margin: 0 0 4px 0; color: #333;">
                            Exchange Student Enrollment Agreement
                        </p>
                        <p style="font-size: 13px; margin: 0; color: #555;">
                            <strong>(Revision Date: 07/27/2026)</strong>
                        </p>
                    </div>
                    <div style="flex-shrink: 0;">
                        <img
                            src="../../../assets/img/BAU_Logo.png"
                            alt="BAU Logo"
                            style="height: 60px; width: auto;"
                            ref="logoImage"
                        />
                    </div>
                </div>
                <hr style="margin: 16px 0;">
                <div class="mb-3 mt-3">
                    <strong style="font-size: 12px;">
                        BY CLICKING THE BOX AT THE END OF THIS DOCUMENT INDICATING YOUR ACCEPTANCE (THE “ACCEPTANCE”) OF THIS ENROLLMENT CONTRACT (THIS “AGREEMENT”), YOU AGREE YOU HAVE READ AND ARE BOUND BY THE TERMS OF THIS AGREEMENT. IF YOU DO NOT AGREE WITH THIS AGREEMENT, YOU MUST NOT ACCEPT THIS AGREEMENT AND MUST NOT CONTINUE WITH THE COURSE REGISTRATION. DOING SO MEANS YOU START AN OFFICIAL WITHDRAWAL PROCESS. IF YOU ACCEPT THIS AGREEMENT, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD, AND ARE BOUND BY, THE TERMS AND CONDITIONS STATED HEREIN.
                    </strong>
                </div>
                <div class="mb-4" style="font-size: 12px;">
                    <p>
                        This Agreement is by and between you and BAY ATLANTIC UNIVERSITY (the “BAU”) and is effective as of the date of Acceptance (the “Effective Date”). It governs your academic and financial rights and responsibilities. BAU reserves the right to change or modify portions of this Agreement at any time, effective during the next semester’s course registration. You will be asked to sign the Agreement for each semester you are enrolled at BAU.
                    </p>
                    <p>
                        <strong>This Agreement Covers the Following Current Period of Attendance for the Program:</strong><br>
                        Period of Attendance Start Date: {{ registrationForm.education_start_date || '______________' }} Period of Attendance End Date: {{ registrationForm.education_end_date || '______________' }}<br>
                        <span style="font-style: italic; color: #666;">Semester start and end dates will be entered here</span>
                    </p>
                    <p>
                        This Agreement covers only the above-referenced current period of attendance for the Program. If the number of semesters to complete the Program extends beyond the above-referenced period of attendance end date, as a condition to further enrollment, you will be required to execute a new Enrollment Agreement with BAU to cover the then-current period of attendance for the Program.
                    </p>
                </div>
            </div>

            <!-- SECTION A -->
            <hr style="margin: 16px 0;">
            <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">A. VERIFICATION OF PERSONAL INFORMATION</h4>
            <p style="font-size: 12px; margin-bottom: 12px;">
                The following information is pulled from the application form you submitted. If you need to update the information, you can click on the Edit Button. (Only after this section, there will be an edit section and then the student will verify the edited version).
            </p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
                <tbody>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; width: 30%; padding: 6px; background-color: #f9f9f9;">Full Name:</td>
                        <td style="padding: 6px; width: 35%;">{{ registrationForm.name }} {{ registrationForm.middle_name ? registrationForm.middle_name + ' ' : '' }}{{ registrationForm.surname }}</td>
                        <td style="font-weight: bold; width: 15%; padding: 6px; background-color: #f9f9f9;">Date of Birth:</td>
                        <td style="padding: 6px; width: 20%;">{{ registrationForm.birthdate ? toLocaleDate(registrationForm.birthdate) : '' }}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">U.S. Mailing Address:</td>
                        <td colspan="3" style="padding: 6px;">
                            {{ registrationForm.apartment ? registrationForm.apartment + ', ' : '' }}{{ registrationForm.contact_address }} {{ registrationForm.contact_city_name ? ', ' + registrationForm.contact_city_name : '' }} {{ registrationForm.contact_state_name ? ', ' + registrationForm.contact_state_name : '' }} {{ registrationForm.contact_zip_code }}
                        </td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Your Home Address (International Student):</td>
                        <td colspan="3" style="padding: 6px;">
                            {{ registrationForm.address }} {{ registrationForm.address_country_name ? ', ' + registrationForm.address_country_name : '' }}
                        </td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Your Personal Email:</td>
                        <td style="padding: 6px;">{{ registrationForm.personal_email }}</td>
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Your U.S. Mobile Phone:</td>
                        <td style="padding: 6px;">{{ registrationForm.mobile_tel ? (registrationForm.mobile_tel_country_code ?? '') + registrationForm.mobile_tel : '' }}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Other Phone:</td>
                        <td style="padding: 6px;">{{ registrationForm.other_phone }}</td>
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Emergency Contact Name:</td>
                        <td style="padding: 6px;">{{ registrationForm.emergency_person_name }}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Emergency Contact Relationship to You:</td>
                        <td style="padding: 6px;">{{ registrationForm.emergency_person_relation }}</td>
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Emergency Contact Tel:</td>
                        <td style="padding: 6px;">{{ registrationForm.emergency_person_phone ? (registrationForm.emergency_person_phone_country_code ?? '') + registrationForm.emergency_person_phone : '' }}</td>
                    </tr>
                    <tr>
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Current Legal Presence Type:</td>
                        <td colspan="3" style="padding: 6px;">{{ registrationForm.legal_presence_type_text }}</td>
                    </tr>
                </tbody>
            </table>
            <p style="font-size: 12px;">☑ I confirm that the information given in this section is true, complete, and accurate</p>

            <!-- SECTION B -->
            <hr style="margin: 16px 0;">
            <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">B. PROGRAM INFORMATION</h4>
            <p style="font-size: 12px; margin-bottom: 12px;">The following information is pulled from the application form you submitted.</p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
                <tbody>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; width: 40%; padding: 6px; background-color: #f9f9f9;">Program of Study:</td>
                        <td style="padding: 6px;">{{ registrationForm.program_name }}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Program Start Date:</td>
                        <td style="padding: 6px;">{{ registrationForm.registration_date }}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Registration Type:</td>
                        <td style="padding: 6px;">{{ registrationForm.registration_type_text }}</td>
                    </tr>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Enrollment Status:</td>
                        <td style="padding: 6px;">{{ registrationForm.student_status_text }}</td>
                    </tr>
                </tbody>
            </table>
            <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section B.</p>

            <!-- SECTION C -->
            <hr style="margin: 16px 0;">
            <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">C. FEES AND CHARGES FOR THE CURRENT PERIOD OF ATTENDANCE</h4>
            <p style="font-size: 12px; margin-bottom: 12px;">The following fees and charges reflect the information during your application process.</p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
                <tbody>
                    <tr style="border-bottom: 1px solid #ddd;">
                        <td style="font-weight: bold; width: 35%; padding: 6px; background-color: #f9f9f9;">Admission Confirmation Deposit:</td>
                        <td style="padding: 6px;">$ {{ registrationForm.admission_confirmation_deposit || 'xxx' }} - Non Refundable (but credited towards tuition).</td>
                    </tr>
                    <tr>
                        <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">OTHER FEES MAY APPLY:</td>
                        <td style="padding: 6px;">For example, requests by students for official university documents (e.g., requests for transcripts, enrollment verifications and academic records) may require payment of the relevant fee(s). Information about the above-mentioned university fees and other fees can be found on Bay Atlantic University’s website at https://bau.edu/admissions/tuition-fees/.</td>
                    </tr>
                </tbody>
            </table>
            <p style="font-size: 12px;">Tuition typically increases by no more than 3% each academic year. Mandatory semester fees increase every two years.</p>
            <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section C.</p>


            <!-- SECTION F -->
            <hr style="margin: 16px 0;">
            <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0;">D. SCHOLARSHIPS/TUITION ASSISTANCE/AWARDS</h4>
            <div style="font-size: 12px;">
                <p>If the student wishes to transfer their SEVIS record to another institution prior to or during the semester, BAU will not refund any portion of their tuition or fees. Additionally, the student will be required to pay a processing fee of $2,000.</p>
            </div>
            <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section D.</p>

          <hr style="margin: 16px 0;">
          <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">E. FINANCIAL GOOD STANDING</h4>
          <div style="font-size: 12px;">
            <p>Financial good standing and a university record clear of holds are required for students to receive services. Upon course registration, students are required to sign a Financial Responsibility Agreement that outlines all financial procedures, obligations, rights, and responsibilities that are summarized below:</p>
            <p>Services, including but not limited to access to courses, transcript issuance, diploma release, class registration (add, drop, withdrawal, etc.), and/or housing, will be immediately suspended for students with a financial balance due or a hold of any kind on their record. Course grades for those students who have not completed their financial requirements until the end of the final exam week will not be entered. This means the credits for these courses will not be earned until the payments are made. If the student’s account remains delinquent, BAU reserves the right to terminate enrollment. Additionally, they will not be cleared for graduation, nor will they receive their official transcripts or diplomas.</p>
            <p>Failure to meet financial obligations to BAU will result in other collection procedures, which include account referral to credit reporting bureaus, private collection agencies, and DC Office of Tax and Revenue, and Office of Attorney General. Past-due accounts are subject to garnishments, liens and judgments, and the withholding of money from tax refunds. In addition to late fees and interest, delinquent accounts will be assessed additional collection costs up to thirty percent of the past due balance, reasonable attorney fees, and other administrative costs. Once an account is referred to a collection agency, payment must be made to the agency, not BAU.</p>
            <p>Late Fee: Failure to make any payment on or before the due date results in a $25 late fee per credit per installment. The late fee will apply only to the tuition. Other fees (for example, activity fee, technology fee, etc.) will be deducted before applying payment for tuition.</p>
            <p>Return Check Fee: A $35 fee will be charged for each unpaid check returned by the bank. Moreover, BAU will recalculate the late fee until the balance is cleared.</p>
            <p>Exceptional Cases: In the documented event of prolonged illness, accident, or death in the immediate family (parents, siblings, children, or spouse) or other exceptional circumstances that make it impractical to complete the program, BAU will work toward a settlement that is reasonable and fair to both parties.</p>
          </div>
          <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section E.</p>


          <!-- SECTION F -->
            <hr style="margin: 16px 0;">
            <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">F. OTHER AGREEMENTS/UNDERSTANDINGS</h4>
            <ol style="font-size: 12px; padding-left: 20px;">
                <li><strong>ACADEMIC CATALOG:</strong> Academic Catalog contains a description of certain policies, procedures, and other information about BAU. BAU reserves the right to change any provision of the catalog at any time. The current catalog is available online and can be accessed at the following website address: https://bau.edu/academic-catalog/. Students are expected to read and be familiar with the information contained in the then-published catalog.</li>
                <li><strong>COMPLIANCE WITH POLICIES, PROCEDURES, AND STANDARDS:</strong> BAU’s policies, procedures, and standards are published on the website, located at https://bau.edu/explore-bau/public-disclosure-policies/all-bau-policies/. Students are expected to know and must comply with and adhere to all applicable policies, procedures, and standards...</li>
                <li><strong>ACADEMIC CALENDAR:</strong> Students should familiarize themselves with BAU’s calendar for holidays and vacations published on the website at https://bau.edu/academic-calendar/...</li>
                <li><strong>NOTICE CONCERNING TRANSFERABILITY OF CREDITS AND CREDENTIALS EARNED AT BAU:</strong> The transferability of credits you earn at BAU is at the complete discretion of the institution to which you may seek to transfer...</li>
                <li><strong>IMPLIED CONSENT:</strong> You acknowledge and agree that BAU or its representatives, affiliates, successors, and related companies may photograph, video or otherwise record you and/or other students...</li>
                <li><strong>COMPLAINTS:</strong> A student or any member of the public may file a complaint about this institution on issues that cannot be resolved by direct negotiation with the school in accordance with its written grievance policy with the Higher Education Licensure Commission (HELC)...</li>
                <li><strong>LEGAL:</strong> The law, including the statutes of limitation, of the District of Columbia will govern this Agreement, the interpretation and enforcement of its terms and any claim or cause of action...</li>
                <li><strong>COPY OF THE ENROLLMENT AGREEMENT:</strong> A copy of this Contract will be emailed to the student upon signing and will be accessible on the student’s MyBAU account.</li>
            </ol>
            <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section F.</p>

            <!-- FINAL ACKNOWLEDGEMENT & ACCEPTANCE -->
            <hr style="margin: 16px 0;">
            <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">FINAL ACKNOWLEDGEMENT &amp; CONTRACT ACCEPTANCE</h4>
            <p style="font-size: 12px;">
                I acknowledge and agree that the terms and conditions of this enrollment agreement are not subject to amendment or modification by oral agreement. I further acknowledge and agree that no oral statements have been made to me that are contrary to what is contained in this enrollment agreement. My signature below certifies that I have read, understand and agree to the terms and conditions contained in this enrollment agreement. This enrollment agreement is legally binding when signed by me and accepted by Bay Atlantic University.
            </p>
            <p style="font-size: 12px;"><strong>CONTRACT ACCEPTANCE</strong></p>
            <p style="font-size: 12px;">
                I, the undersigned, have read and understood this agreement and agree that it supersedes all prior or contemporaneous verbal or written agreements and may not be modified without the written agreement of both the student and the School Official. I also understand that if I default on this agreement, I will be responsible for paying any collection fees or attorney fees incurred by BAU.
            </p>
            <p style="font-size: 12px;">
                By clicking the box below, I hereby affix my electronic signature, indicating my intent to be legally bound by the terms of this agreement. My signature below signifies that I have read, understood, and acknowledged all aspects of this agreement, and I have recognized my legal responsibilities under this contract.
            </p>

            <table style="width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12px; border: 1px solid #ddd;">
                <tbody>
                    <tr>
                        <td style="font-weight: bold; width: 60%; padding: 8px;">[{{ registrationForm.name }} {{ registrationForm.middle_name ? registrationForm.middle_name + ' ' : '' }}{{ registrationForm.surname }}]</td>
                        <td style="font-weight: bold; padding: 8px;">Date: {{ currentDate }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div style="display: flex; justify-content: flex-end;" class="no-button mt-3">
            <b-button variant="primary" @click="submitData" :disabled="loading">
                <b-spinner small v-if="loading"></b-spinner>
                {{ $t('send') }}
            </b-button>
        </div>
    </div>
</template>
<script>
import EnrollmentAgreementForm from "@/services/EnrollmentAgreementForm";
import {mapGetters} from "vuex";
import moment from "moment/moment";
import LoadingButton from "@/components/elements/LoadingButton.vue";


export default {
  components: {LoadingButton},
  data() {
    return {
      disabled:false,
      loading:false,
      registrationForm: {},
      data:[],
    }
  },
  computed: {
    ...mapGetters({
      studentData: "enrollmentAgreementForm/formData"
    }),
    currentDate(){
      return moment().format("MMMM Do YYYY")
    }
  },
  created() {
    this.getData()
  },
  methods: {
    async submitData() {
      this.loading = true;
      try {
        await this.sendToBackend();
      } catch (error) {
        console.error('Error in submitData:', error);
      } finally {
        this.loading = false;
      }
    },
    async getData() {
      const response = await EnrollmentAgreementForm.getStudentInformation(this.studentData.id)
      const data = response.data.data;
      this.data.push(
          {
            key: 'Admission Confirmation Deposit',
            value: `$${data.admission_confirmation_deposit?data.admission_confirmation_deposit:0} – Non Refundable (but credited towards tuition).`,
          },
          {
            key: 'Tuition Deposit',
            value: `$${data.tuition_deposit?data.tuition_deposit:0} – Non Refundable (but credited towards tuition).`,
          },
          {
            key: 'Tuition per Credit ',
            value: `$${data.tuition_per_credit?data.tuition_per_credit:0}`,
          },
          {
            key: 'MANDATORY FEES',
            value: `Student Activities and Services Fee: $${data.services_fee}<br>Technology Fee: $${data.technology_fee}<br>Student ID Fee: $${data.student_id_card_fee ?? 0} (only if this is your first semester at BAU)`,
          },
          {
            key: 'OTHER FEES MAY APPLY',
            value: `For example, requests by students for official university documents (e.g., requests for transcripts, enrollment verifications and academic records) may require payment of the relevant <strong>university fees</strong>. Information about the above-mentioned <strong>university fees</strong> and other fees can be found on <a href="https://bau.edu/admissions/tuition-fees/" target="_blank">Bay Atlantic University's website</a>.`,
          },
      )
      this.registrationForm = {
        ...this.studentData,
        registration_date: data.registration_date,
        program_name: data.program_name,
        registration_semester_academic_year: data.registration_semester_academic_year,
        anticipated_end_date: data.anticipated_end_date,
        registration_type_text: data.registration_type_text,
        total_credit_required_for_graduation: data.total_credit_required_for_graduation,
        tuition_per_credit: data.tuition_per_credit,
        total_discount_per_credit: data.discount_per_credit*data.total_credit_required_for_graduation
      }
    },
    async sendToBackend() {
      const config = {
        // html: base64String,
        contact_address:this.studentData.contact_address,
        contact_state_id:this.studentData.contact_state_id,
        contact_city_id:this.studentData.contact_city_id,
        contact_zip_code:this.studentData.contact_zip_code,
        apartment:this.studentData.apartment,
        address_country_id:this.studentData.address_country_id,
        address:this.studentData.address,
        verification_code:this.studentData.verification_code,
        personal_email:this.studentData.personal_email,
        mobile_tel:this.studentData.mobile_tel,
        mobile_tel_country_code:this.studentData.mobile_tel_country_code,
        home_tel:this.studentData.home_tel,
        home_tel_country_code:this.studentData.home_tel_country_code,
        emergency_person_phone_country_code:this.studentData.emergency_person_phone_country_code,
        emergency_person_relation:this.studentData.emergency_person_relation,
        emergency_person_name:this.studentData.emergency_person_name,
        emergency_person_phone:this.studentData.emergency_person_phone,
        birthdate:this.studentData.birthdate,
      }
      try {
        const response = await EnrollmentAgreementForm.sendForm(config);
        if (response.data.success===true) {
          this.$toast.success(this.$t('form_submission_successful'))
          this.$emit('sendSuccessForm', true)
          this.clearForm()
          this.$router.push('/dashboard');
        } else {
          this.$toast.success(this.$t('api.' + response.data.message));
          this.clearForm()
        }
      } catch (error) {
        if (error.data.message==="SMS_CODE_ERROR"){
          this.$toast.error(this.$t('api.' + error.data.message));
          this.$emit('errorSmsCode',true)

        }else{
          this.$toast.error(this.$t('api.' + error.data.message));
        }
      }
    },
    async clearForm() {
      await this.$store.dispatch('enrollmentAgreementForm/deleteFormData');
    },

  },


};
</script>
<style scoped>
@media print {
  body {
    -webkit-print-color-adjust: exact !important;
  }
  .no-button {
    display: none;
  }
}
</style>
